import { fromEvent, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    findParent,
    focus,
    KeyboardNavigator as IKeyboardNavigator,
    KeyKeyCodeMap,
    KeyTypeMap,
    ResultAction,
} from '../common/keyboard.model';
import { takeUntilDestroy } from '../common/take-until-destroy.decorator';

export class KeyboardNavigator implements IKeyboardNavigator {
    toggleNavigation: ($event: KeyboardEvent) => void;
    openDropdown: (el: HTMLElement | null) => void;
    closeDropdowns: () => void;
    aDropdownIsOpen: () => boolean;
    isDropdown: (el: HTMLElement | null) => boolean;
    private readonly _sub: Subscription;
    private _lastFocused: HTMLElement;
    private _lastFocusedDropdown: HTMLElement;

    constructor(private readonly _root: HTMLElement) {
        this._sub = fromEvent(document, 'click')
            .pipe(
                tap(() => {
                    if (this._lastFocused) {
                        this._lastFocused.tabIndex = 0;
                        this._lastFocused = null;
                        // no return
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    _handleEvent($event: KeyboardEvent): ResultAction {
        const el = $event.target as HTMLElement;
        if (!el) return ResultAction.IGNORE;

        if ($event.keyCode === KeyKeyCodeMap.Tab) {
            if ($event.type === KeyTypeMap.Down) {
                // no return
            }
            if ($event.type === KeyTypeMap.Up) {
                // no return
            }
            // no return
        }

        if ($event.type === KeyTypeMap.Up) return ResultAction.IGNORE;

        if (
            $event.keyCode === KeyKeyCodeMap.Enter ||
            $event.keyCode === KeyKeyCodeMap.Space
        ) {
            // no return
        }

        if ($event.keyCode === KeyKeyCodeMap.Escape) {
            this.closeDropdowns();
            focus(el, this._lastFocusedDropdown);
            this._lastFocused = this._lastFocusedDropdown;
            // no return
        }

        if ($event.keyCode === KeyKeyCodeMap.ArrowUp) {
            return ResultAction.NO_DEFAULT;
        }

        if ($event.keyCode === KeyKeyCodeMap.ArrowDown) {
            this.openDropdown(this._lastFocusedDropdown);
            return ResultAction.NO_DEFAULT;
        }

        if (
            $event.keyCode === KeyKeyCodeMap.ArrowLeft ||
            $event.keyCode === KeyKeyCodeMap.ArrowRight
        ) {
            const root = findParent(el, '[role=menubar]') || this._root;
            const els: HTMLElement[] = Array.from(
                root.querySelectorAll('.nav-item [role=menuitem]'),
            );
            const index = els.indexOf(this._lastFocused || el);
            const goToPrevious = $event.keyCode === KeyKeyCodeMap.ArrowLeft;
            const nextIndex = index + (goToPrevious ? -1 : 1);
            const finalIndex =
                nextIndex >= els.length
                    ? 0
                    : nextIndex < 0
                    ? els.length - 1
                    : nextIndex;
            const next = els[finalIndex];
            focus(els, next);
            const aDropdownIsOpen = this.aDropdownIsOpen();
            this._lastFocused = next;
            if (this.isDropdown(next)) {
                this._lastFocusedDropdown = next;
                if (aDropdownIsOpen) {
                    this.openDropdown(this._lastFocusedDropdown);
                }
            }
            return ResultAction.BOTH;
        }

        return ResultAction.IGNORE;
    }

    handleEvent($event: KeyboardEvent): void {
        const ret = this._handleEvent($event);
        switch (ret) {
            case ResultAction.NO_PROPAGATE:
                $event.stopPropagation();
                break;
            case ResultAction.NO_DEFAULT:
                $event.preventDefault();
                break;
            case ResultAction.BOTH:
                $event.stopPropagation();
                $event.preventDefault();
        }
    }

    destroy(): void {
        this._sub.unsubscribe();
    }
}
